var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext, useEffect, useState } from 'react';
import MUIDataTable from 'mui-datatables';
import { Box, Chip, IconButton, Link, MenuItem, Select, Tooltip } from '@mui/material';
import { getContacts, getFirmUsers, getOrganisations, removeXeroConnection, syncTenants, tagOrganisation, untagOrganisation, updateOrganisationContact, updateOrganisationManager, updateOrganisations, } from '../rest/BackendApi';
import { ThemeProvider } from '@mui/material/styles';
import AppConfig from '../AppConfig';
import SyncIcon from '@mui/icons-material/Sync';
import EditIcon from '@mui/icons-material/Edit';
import { customFilterWithPrefix, getMuiTheme } from '../components/Datatable';
import AddIcon from '@mui/icons-material/LibraryAddOutlined';
import Loading from '../components/Loading';
import { PeriodEnum } from '../model/PeriodEnum';
import { humanize } from '../util/StringUtil';
import NoOrganisation from '../components/NoOrganisation';
import { SectionTitle, StyledMuiLink, SubText } from '../components/StyledComponent';
import OrgSyncStatusDisplay from '../components/SyncStatus';
import dayjs from 'dayjs';
import UserContext from '../UserContext';
import ConfirmDialog from '../components/ConfirmDialog';
import DialogSelect from '../components/DialogSelect';
import SupportLink from '../components/SupportLink';
import XeroLink, { ExternalLink } from '../components/XeroLink';
import styles from '../style/common.module.css';
import { AddTextField } from '../components/AddTextField';
import AddTagIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { getDatatableColumnConfig, getDatatableConfig } from '../util/LocalStorageUtil';
import { getFullName } from '../util/DataUtil';
import { displayDate } from '../util/TimeUtil';
import MessageContext, { empty, error, success } from '../MessageContext';
import { CheckBox } from '../components/CheckBox';
import { hasFeature } from '../business/Subscription';
import { DisplayWithSeeMoreChip } from '../components/DisplayWithSeeMoreChip';
export default function OrganisationPage() {
    var _a, _b, _c, _d, _e, _f, _g;
    const [loading, setLoading] = useState(true);
    const [organisations, setOrganisations] = useState([]);
    const [disconnectConfirmData, setDisconnectConfirmData] = useState({ open: false });
    const [selectDialogData, setSelectDialogData] = useState({
        open: false,
        loadData: () => __awaiter(this, void 0, void 0, function* () {
            return [];
        }),
    });
    const userContext = useContext(UserContext);
    const messageContext = useContext(MessageContext);
    const user = userContext.user;
    const shouldShowOpenAccess = hasFeature(user, 'MultiUser') && ((_a = user === null || user === void 0 ? void 0 : user.firm) === null || _a === void 0 ? void 0 : _a.openAccess);
    const shouldShowXpm = hasFeature(user, 'XPM');
    function loadOrganisations() {
        return __awaiter(this, void 0, void 0, function* () {
            setLoading(true);
            const organisations = yield getOrganisations();
            if (organisations) {
                setOrganisations(organisations);
            }
            setLoading(false);
        });
    }
    const onSyncReady = (syncStatus, attempt) => {
        if (syncStatus == null) {
            return;
        }
        // If attempt was greater than 1 or attempt 1 completed less than few seconds ago we refresh
        let syncCompletionTimeInMs = dayjs().diff(dayjs.utc(syncStatus.updateDate));
        if (attempt > 1 || syncCompletionTimeInMs < 5000) {
            loadOrganisations();
        }
    };
    function disconnectOrganisation(orgId) {
        return __awaiter(this, void 0, void 0, function* () {
            setLoading(true);
            let removed = yield removeXeroConnection(orgId);
            if (!removed) {
                messageContext.setMessage(error('Failed to disconnect organisation from Xero. Log out and log back in if the problem persists.'));
                setLoading(false);
            }
            else {
                if (userContext.refreshUser) {
                    userContext.refreshUser();
                }
                loadOrganisations();
            }
        });
    }
    function syncOrgs(selected, setSelected) {
        return __awaiter(this, void 0, void 0, function* () {
            setLoading(true);
            messageContext.setMessage(empty());
            const updatedEntries = yield syncTenants(selected.map(row => row.id));
            if (updatedEntries) {
                const mappedEntry = new Map(updatedEntries.map(element => [element.id, element]));
                setOrganisations(organisations.map(entry => mappedEntry.get(entry.id) || entry));
                setSelected([]);
                messageContext.setMessage(success('Org synced successfully'));
            }
            else {
                messageContext.setMessage(error('Failed to sync with Xero. Log out and log back in if the problem persists.'));
            }
            setLoading(false);
        });
    }
    function updateOrganisationAfterPostApiCall(org) {
        setOrganisations(organisations.map(o => (o.id === org.id ? org : o)));
    }
    function updatePaygwPeriod(org, event) {
        return __awaiter(this, void 0, void 0, function* () {
            setLoading(true);
            messageContext.setMessage(empty());
            try {
                const updatedOrg = yield updateOrganisations(org.id, { paygwPeriod: event.target.value });
                updateOrganisationAfterPostApiCall(updatedOrg);
            }
            catch (e) {
                messageContext.setMessage(error('Failed to update PAYGW Period with Xero. Log out and log back in if the problem persists.'));
            }
            setLoading(false);
        });
    }
    function updateAutoEmailReminder(org, checked) {
        return __awaiter(this, void 0, void 0, function* () {
            messageContext.setMessage(empty());
            try {
                const updatedOrg = yield updateOrganisations(org.id, { autoReminderBasDue: checked });
                updateOrganisationAfterPostApiCall(updatedOrg);
            }
            catch (e) {
                messageContext.setMessage(error('Failed to update auto reminder email setting.'));
            }
        });
    }
    useEffect(() => {
        const fetchData = () => __awaiter(this, void 0, void 0, function* () {
            try {
                loadOrganisations();
            }
            catch (error) {
                console.log('There was an error', error);
            }
        });
        fetchData();
    }, []);
    const columns = [
        {
            name: 'name',
            label: 'Legal Name',
            options: {
                filter: true,
                sort: true,
                customBodyRenderLite: (dataIndex) => {
                    const row = organisations[dataIndex];
                    return (_jsx(_Fragment, { children: _jsx("div", { style: { display: 'flex', alignItems: 'center', minWidth: '250px' }, children: _jsxs("div", { children: [row.name + ' ', _jsx(XeroLink, { shortCode: row.shortCode }), row.abn && _jsxs(SubText, { children: ["ABN: ", row.abn] })] }) }) }));
                },
            },
        },
        {
            name: 'manager',
            label: 'Client Manager',
            options: Object.assign(Object.assign({ filter: true, sort: true }, customFilterWithPrefix('Manager')), { filterOptions: {
                    names: [...new Set(organisations.map(org => (org.manager ? getFullName(org.manager) : 'None')))].sort(),
                    logic: (location, filters, row) => {
                        const manager = location;
                        if (manager == null) {
                            return !filters.includes('None');
                        }
                        if (filters.length)
                            return !filters.includes(getFullName(manager));
                        return false;
                    },
                }, customBodyRenderLite: (dataIndex) => {
                    const row = organisations[dataIndex];
                    const EditLink = (props) => (_jsx(StyledMuiLink, { style: { cursor: 'pointer' }, onClick: () => __awaiter(this, void 0, void 0, function* () {
                            var _a, _b;
                            try {
                                setSelectDialogData({
                                    open: true,
                                    title: 'Select organisation manager',
                                    emptyState: '',
                                    selectFunction: (userId) => __awaiter(this, void 0, void 0, function* () {
                                        try {
                                            messageContext.setMessage(empty());
                                            const updatedOrg = yield updateOrganisationManager(row.id, userId);
                                            setOrganisations(organisations.map(o => (o.id === updatedOrg.id ? updatedOrg : o)));
                                            messageContext.setMessage(success('Organisation manager updated'));
                                        }
                                        catch (e) {
                                            messageContext.setMessage(error(_jsxs("span", { children: ["Failed to update organisation manager, please try again or ", _jsx(SupportLink, {})] })));
                                        }
                                    }),
                                    initialValue: (_b = (_a = row.manager) === null || _a === void 0 ? void 0 : _a.id) === null || _b === void 0 ? void 0 : _b.toString(),
                                    loadData: () => __awaiter(this, void 0, void 0, function* () {
                                        var _a, _b;
                                        const users = ((_a = user === null || user === void 0 ? void 0 : user.firm) === null || _a === void 0 ? void 0 : _a.id) ? yield getFirmUsers((_b = user === null || user === void 0 ? void 0 : user.firm) === null || _b === void 0 ? void 0 : _b.id) : [];
                                        return users.map((user) => {
                                            return {
                                                key: user.id.toString(),
                                                value: getFullName(user),
                                            };
                                        });
                                    }),
                                });
                            }
                            catch (exception) {
                                messageContext.setMessage(error('Couldn\'t load users for that organisation'));
                            }
                        }), children: row.manager ? _jsx(EditIcon, { fontSize: "small" }) : 'Set manager' }));
                    return (_jsx("div", { className: styles.centered, children: row.manager ? (_jsxs("div", { className: styles.centered, children: [row.manager.givenName, " ", row.manager.familyName, " ", _jsx(EditLink, {})] })) : (_jsx(EditLink, {})) }));
                } }),
        },
        {
            name: 'contact',
            label: 'Contact',
            options: {
                filter: false,
                sort: false,
                customBodyRenderLite(dataIndex) {
                    const row = organisations[dataIndex];
                    const EditLink = (props) => (_jsx(StyledMuiLink, { style: { cursor: 'pointer' }, onClick: () => __awaiter(this, void 0, void 0, function* () {
                            var _a;
                            try {
                                setSelectDialogData({
                                    open: true,
                                    title: 'Select organisation contact',
                                    emptyState: 'No contact found for this organisation',
                                    selectFunction: (email) => __awaiter(this, void 0, void 0, function* () {
                                        try {
                                            messageContext.setMessage(empty());
                                            const updatedOrg = yield updateOrganisationContact(row.id, email);
                                            setOrganisations(organisations.map(o => (o.id === updatedOrg.id ? updatedOrg : o)));
                                            messageContext.setMessage(success('Organisation contact updated'));
                                        }
                                        catch (e) {
                                            messageContext.setMessage(error(_jsxs("span", { children: ["Failed to update organisation contact, please try again or ", _jsx(SupportLink, {})] })));
                                        }
                                    }),
                                    initialValue: ((_a = row.contact) === null || _a === void 0 ? void 0 : _a.email) || '',
                                    loadData: () => __awaiter(this, void 0, void 0, function* () {
                                        const contacts = yield getContacts(row.id);
                                        return contacts.map((contact) => {
                                            return {
                                                key: contact.email,
                                                value: contact.firstName + ' ' + contact.lastName,
                                            };
                                        });
                                    }),
                                });
                            }
                            catch (exception) {
                                messageContext.setMessage(error('Couldn\'t load contacts for that organisation'));
                            }
                            setLoading(false);
                        }), children: row.contact ? _jsx(EditIcon, { fontSize: "small" }) : 'Set contact' }));
                    return (_jsx("div", { className: styles.centered, children: row.contact ? (_jsxs("div", { className: styles.centered, children: [_jsxs("div", { children: [_jsxs("div", { className: styles.centered, children: [row.contact.firstName, " ", row.contact.lastName] }), _jsx(SubText, { children: row.contact.email })] }), _jsx(EditLink, {})] })) : (_jsx(EditLink, {})) }));
                },
            },
        },
        {
            name: 'gstPeriod',
            label: 'GST Period',
            options: {
                filter: true,
                sort: true,
                setCellProps: () => {
                    return { align: 'center' };
                },
                customBodyRender(value) {
                    return humanize(value);
                },
            },
        },
        {
            name: 'paygwPeriod',
            label: 'PAYGW Period',
            options: {
                filter: true,
                sort: true,
                setCellProps: () => {
                    return { align: 'center' };
                },
                customBodyRenderLite(dataIndex) {
                    const row = organisations[dataIndex];
                    return (_jsxs(Select, { size: "small", sx: { mt: '4px', mb: '4px', width: '120px' }, id: "paygw-period-select", value: row.paygwPeriod, onChange: event => updatePaygwPeriod(row, event), children: [_jsx(MenuItem, { value: 'MONTHLY', children: PeriodEnum.MONTHLY }), _jsx(MenuItem, { value: 'QUARTERLY', children: PeriodEnum.QUARTERLY })] }));
                },
            },
        },
        {
            name: 'autoReminderBasDue',
            label: 'Auto reminder email',
            options: {
                hint: 'A reminder email will be sent after 14 & 28 days if there are any unreconciled bank transactions',
                filter: true,
                sort: true,
                display: true,
                setCellProps: () => {
                    return { align: 'center' };
                },
                customBodyRenderLite(dataIndex) {
                    const row = organisations[dataIndex];
                    return _jsx(CheckBox, { onSubmit: checked => updateAutoEmailReminder(row, checked), checked: row.autoReminderBasDue });
                },
            },
        },
        {
            name: 'basPeriod',
            label: 'Bas Period',
            options: {
                filter: true,
                sort: true,
                setCellProps: () => {
                    return { align: 'center' };
                },
                customBodyRender(value) {
                    return humanize(value);
                },
            },
        },
        {
            name: 'gstBasis',
            label: 'GST Basis',
            options: {
                filter: true,
                sort: true,
                setCellProps: () => {
                    return { align: 'center' };
                },
                customBodyRender(value) {
                    return humanize(value);
                },
            },
        },
        {
            name: 'tags',
            label: 'Tags',
            options: {
                sort: false,
                filter: true,
                filterOptions: {
                    names: [...new Set(organisations.flatMap(org => org.tags.map(t => t.name)))].sort(),
                    logic: (tags, filters) => {
                        const tagNames = tags.map(t => t.name);
                        return !filters.some((filter) => tagNames.includes(filter));
                    },
                },
                customFilterListOptions: {
                    render: (value) => {
                        return 'Tag: ' + value;
                    },
                },
                customBodyRenderLite(dataIndex) {
                    var _a;
                    const row = organisations[dataIndex];
                    return (_jsxs("div", { style: { flexWrap: 'wrap', maxWidth: '200', gap: '4px' }, className: styles.left, children: [_jsx(AddTextField, { addIcon: _jsx(AddTagIcon, { sx: { color: '#007ca3', cursor: 'pointer', verticalAlign: 'middle' } }), maxLength: 32, textEditLabel: "Tag Name", onSubmit: (value) => __awaiter(this, void 0, void 0, function* () {
                                    const org = yield tagOrganisation(row.id, value);
                                    updateOrganisationAfterPostApiCall(org);
                                }) }), (_a = row.tags) === null || _a === void 0 ? void 0 : _a.map(tag => (_jsx(Chip, { label: tag.name, variant: "outlined", onDelete: () => __awaiter(this, void 0, void 0, function* () {
                                    const org = yield untagOrganisation(row.id, tag.name);
                                    updateOrganisationAfterPostApiCall(org);
                                }) }, tag.id)))] }));
                },
            },
        },
        {
            name: 'gstAccountName',
            label: 'GST Account',
            options: {
                filter: false,
                sort: false,
                display: ((_b = getDatatableColumnConfig('organisationConfig', 'gstAccountName')) === null || _b === void 0 ? void 0 : _b.display) || 'false',
            },
        },
        {
            name: 'paygWitholdingPayableName',
            label: 'PAYG Account',
            options: {
                filter: false,
                sort: false,
                display: ((_c = getDatatableColumnConfig('organisationConfig', 'paygWitholdingPayableName')) === null || _c === void 0 ? void 0 : _c.display) || 'false',
            },
        },
        {
            name: 'wagesPayableAccountName',
            label: 'Wages Payable Account',
            options: {
                filter: false,
                sort: false,
                display: ((_d = getDatatableColumnConfig('organisationConfig', 'wagesPayableAccountName')) === null || _d === void 0 ? void 0 : _d.display) || 'false',
            },
        },
        {
            name: 'superPayableAccountName',
            label: 'Superannuation Payable Account',
            options: {
                filter: false,
                sort: false,
                display: ((_e = getDatatableColumnConfig('organisationConfig', 'superPayableAccountName')) === null || _e === void 0 ? void 0 : _e.display) || 'false',
            },
        },
        {
            name: 'periodLockDate',
            label: 'User Lock Date',
            options: {
                filter: false,
                sort: true,
                setCellProps: () => {
                    return { align: 'center' };
                },
                customBodyRender: (value) => {
                    return value ? displayDate(value) : '';
                },
            },
        },
        {
            name: 'endOfYearLockDate',
            label: 'Adviser Lock Date',
            options: {
                filter: false,
                sort: true,
                setCellProps: () => {
                    return { align: 'center' };
                },
                customBodyRender: (value) => {
                    return value ? displayDate(value) : '';
                },
            },
        },
        shouldShowOpenAccess && {
            name: 'connectedToCurrentUser',
            label: 'Connected by',
            options: {
                hint: 'Connected from xero by your user directly or another user in your firm',
                filter: true,
                sort: true,
                setCellProps: () => {
                    return { align: 'center' };
                },
                display: ((_f = getDatatableColumnConfig('organisationConfig', 'connectedToCurrentUser')) === null || _f === void 0 ? void 0 : _f.display) || 'false',
                customBodyRender(value) {
                    return value ? 'You' : 'Firm';
                },
            },
        },
        shouldShowXpm && {
            name: 'xpmClient',
            label: 'Xpm client',
            options: {
                setCellProps: () => {
                    return { align: 'center' };
                },
                display: ((_g = getDatatableColumnConfig('organisationConfig', 'xpmClient')) === null || _g === void 0 ? void 0 : _g.display) || 'false',
                customBodyRender(value) {
                    return value ? _jsx(ExternalLink, { link: value.xpmUrl, label: "Xpm" }) : '';
                },
            },
        },
        {
            name: 'action',
            label: ' ',
            options: {
                download: false,
                sort: false,
                filter: false,
                viewColumns: false,
                setCellProps: () => {
                    return { align: 'center' };
                },
                customBodyRenderLite: (dataIndex) => {
                    const row = organisations[dataIndex];
                    return (_jsx(_Fragment, { children: row.connectedToCurrentUser ? _jsx(Link, { style: { cursor: 'pointer', whiteSpace: 'nowrap' }, onClick: () => setDisconnectConfirmData({
                                open: true,
                                message: `Are you sure you want to disconnect the organisation "${row.name}"`,
                                disconnectFunction: () => disconnectOrganisation(row.id),
                            }), children: "Disconnect from Xero" })
                            :
                                _jsx(SubText, { children: _jsx("i", { children: _jsx(DisplayWithSeeMoreChip, { prefix: "Connected by ", rows: row.connectedUsers.map((s => getFullName(s))), nbRowsDisplayed: 1, separator: '\n' }) }) }) }));
                },
            },
        },
    ].filter(Boolean);
    const options = {
        filterType: 'dropdown',
        enableNestedDataAccess: '.',
        rowsPerPageOptions: [25, 50, 100],
        rowsPerPage: getDatatableConfig('organisationConfig', 'rowsPerPage') || 50,
        print: false,
        storageKey: 'organisationConfig',
        tableBodyHeight: 'calc(100vh - 200px)',
        fixedSelectColumn: true,
        fixedHeader: true,
        setTableProps: () => {
            return {
                // material ui v5 only
                size: 'small',
                //border: '1'
            };
        },
        customToolbarSelect: function (selectedRows, _, setSelectedRows) {
            if (selectedRows.data.length > 0) {
                const selectedData = selectedRows.data.map(row => organisations[row.dataIndex]);
                return (_jsx("div", { children: _jsx(Tooltip, { title: "Sync with xero", children: _jsx(IconButton, { onClick: () => syncOrgs(selectedData, setSelectedRows), children: _jsx(SyncIcon, {}) }) }) }));
            }
        },
    };
    return (_jsxs(ThemeProvider, { theme: getMuiTheme(), children: [_jsx(OrgSyncStatusDisplay, { syncReadyCallback: onSyncReady }), loading ? (_jsx(Loading, {})) : organisations.length === 0 ? (_jsxs("div", { style: { padding: '10px' }, children: [_jsx(SectionTitle, { children: "Organisations" }), _jsx(NoOrganisation, { alignItems: 'left' })] })) : (_jsxs("div", { children: [_jsx(ConfirmDialog, { title: "Confirmation", open: disconnectConfirmData.open, onConfirm: () => disconnectConfirmData.disconnectFunction && disconnectConfirmData.disconnectFunction(), setOpen: (open) => setDisconnectConfirmData(Object.assign(Object.assign({}, disconnectConfirmData), { open })), cancelText: "Keep", confirmText: "Disconnect", children: disconnectConfirmData.message }), _jsx(DialogSelect, { setOpen: (open) => setSelectDialogData(Object.assign(Object.assign({}, selectDialogData), { open })), open: selectDialogData.open, loadData: selectDialogData.loadData, initialValue: selectDialogData.initialValue, onSelect: selectDialogData.selectFunction, title: selectDialogData.title || '', emptyTitle: selectDialogData.emptyState || '' }), _jsx(Box, { display: "flex", justifyContent: "center", alignItems: "center", paddingBottom: 2, marginTop: "2px", children: _jsx(StyledMuiLink, { underline: "none", href: AppConfig.oauthConnectionsPath, children: _jsxs(Box, { display: "flex", gap: "5px", children: [_jsx(Box, { sx: { height: '24px' }, children: _jsx(AddIcon, {}) }), _jsx(Box, { children: "Connect Xero Organisation(s)" })] }) }) }), _jsx(MUIDataTable, { title: _jsx(SectionTitle, { children: "Organisations" }), data: organisations || [], columns: columns, options: options })] }))] }));
}
